import { CommunicationTypes } from "../constants/communicationTypes";
import { DefaultOauthLink, LoginLink } from "../constants/general";
import { CloakService } from "../services/cloakService";
import { VerifyCodeService } from "../services/verifyCodeService";
import { DomUtils } from "../utils/domUtils";
import { LocationUtils } from "../utils/locationUtils";
import { ValidatedInput } from "../components/validatedInput";
import { BaseController } from "./base";
import { DeviceUtils } from "../utils/deviceUtils";

export const VerifyCodeConstants = {
	sentToLabel: "sentToLabel",
	expirationLabel: "expirationLabel",
	communicationTypeLabel: "communicationTypeLabel",
	actionTypeLabel: "actionTypeLabel",
	verifyCodeForm: "verifyCodeForm",
	token: "token",
	code: "code",
	verifyCodeButton: "verifyCodeButton",
	resendCodeButton: "resendCodeButton",
	sendEmail: "sendEmail",
	logInLink: "logInLink",
	resendCodeForm: "resendCodeForm",
	didntGetCodeContainer: "didntGetCodeContainer",
	sendEmailVerificationContainer: "sendEmailVerificationContainer",
	sendEmailVerificationSpacer: "sendEmailVerificationSpacer",
	sendEmailVerificationLink: "sendEmailVerificationLink",
	sendVerificationEmailForm: "sendVerificationEmailForm",
	rcTokenHidden: "rcTokenHidden",
	rdMethodHidden: "rdMethodHidden",
	sveTokenHidden: "sveTokenHidden",
	sveMethodHidden: "sveMethodHidden",
	recaptcha: "recaptcha",
	loginLinkContainer: "loginLinkContainer",
	registerDaysContainer: "registerDaysContainer",
	registerDaysLabel: "registerDaysLabel",
	autoCompleteInterval: 200
};

export class VerifyCodeController extends BaseController {
	constructor() {
		super();

		this._verifyCodeService = new VerifyCodeService();

		this._signIn = null;
		this._loginLink = null;
		this._method = null;
		this._actionTypeLabel = null;
		this._verifyEndpoint = null;
		this._formAction = null;
		this._sentTo = null;
		this._expiration = null;
		this._registerDays = null;
		this._isCodeAuth = null;
		this._canResend = null;
		this._registerDays = null;
		this._resendLink = null;
		this._token = null;
		this._recaptchaSiteKey = null;
		this._returnUrl = null;

		this._formSubmitBlocked = true;
	}

	Init() {
		this._GetContextData();

		this._SetHeaderLabels(this._method, this._actionTypeLabel);
		this._SetSentToLabel(this._sentTo);
		this._SetExpirationLabel(this._expiration);
		this._SetupForm();
		this._SetupValidation();
		this._SetupResend();
		this._SetupRegisterDays(this._registerDays);

		this._SetupLoginLink(this._loginLink);
		this._ToggleSendEmailVerification(this._canResend);

		this._BindEvents();

		this._SetFocusOnCodeInput();
	}

	_GetContextData() {
		this._returnUrl = window.returnUrl;
		this._signIn = LocationUtils.GetQueryParam("signin");

		if (!!this._returnUrl) {
			this._loginLink = this._returnUrl;
		}
		else if (!!this._signIn) {
			this._loginLink = `${LoginLink}${this._signIn}`;
		}
		else {
			this._loginLink = DefaultOauthLink;
		}

		this._method = window.method;
		this._actionTypeLabel = window.actionTypeLabel;
		this._verifyEndpoint = window.verify;
		this._formAction = window.formAction;
		this._sentTo = window.sentTo;
		this._expiration = window.expiration;
		this._registerDays = window.registerDays;
		this._isCodeAuth = window.isCodeAuth;
		this._canResend = window.canResend;
		this._registerDays = window.registerDays;
		this._resendLink = window.resendLink;
		this._token = window.token;
		this._recaptchaSiteKey = window.recaptchaSiteKey || null;
	}

	_SetHeaderLabels(method, actionTypeLabel) {
		const communicationTypeLabel = method === CommunicationTypes.email ? "oauth.email" : "oauth.phone";

		this._localeService.SetElementTranslation(VerifyCodeConstants.communicationTypeLabel, communicationTypeLabel);
		this._localeService.SetElementTranslation(VerifyCodeConstants.actionTypeLabel, actionTypeLabel);
	}

	_SetSentToLabel(sentTo) {
		const sentToLabel = DomUtils.GetElement(VerifyCodeConstants.sentToLabel);
		sentToLabel.innerText = sentTo;
	}

	_SetExpirationLabel(expirationInMinutes) {
		expirationInMinutes = Number(expirationInMinutes);
		if (isNaN(expirationInMinutes) || expirationInMinutes <= 0) {
			expirationInMinutes = 10;
		}

		const expirationLabel = DomUtils.GetElement(VerifyCodeConstants.expirationLabel);
		expirationLabel.innerText = `${expirationInMinutes}:00`;

		let counter = expirationInMinutes * 60;

		const interval = setInterval(() => {
			counter--;
			expirationLabel.innerText = `${String(Math.floor(counter / 60)).padStart(2, "0")}:${String(counter % 60).padStart(2, "0")}`;

			if (counter <= 0) {
				clearInterval(interval);
			}
		}, 1000);
	}

	_SetupLoginLink(loginLink) {
		const logInLinkElement = DomUtils.GetElement(VerifyCodeConstants.logInLink);
		logInLinkElement.href = loginLink;
	}

	_SetFocusOnCodeInput() {
		this._FocusElement(VerifyCodeConstants.code);
	}

	_SetupForm() {
		const verifyCodeFormElement = DomUtils.GetElement(VerifyCodeConstants.verifyCodeForm);
		verifyCodeFormElement.action = `${this._formAction}?signin=${this._signIn}`;

		const overriddenFormSubmit = this._BlockOriginalFormSubmit.bind(this);

		verifyCodeFormElement.originalSubmit = verifyCodeFormElement.submit;
		verifyCodeFormElement.submit = overriddenFormSubmit;
		verifyCodeFormElement.addEventListener("submit", overriddenFormSubmit);
	}

	_BlockOriginalFormSubmit(e) {
		if (this._formSubmitBlocked === true) {
			e?.preventDefault();
			e?.stopPropagation();

			this._OnVerifyCodeClicked();
		}
		else {
			const verifyCodeFormElement = DomUtils.GetElement(VerifyCodeConstants.verifyCodeForm);
			verifyCodeFormElement.originalSubmit();
		}
	}

	_SetupResend() {
		const resendCodeFormElement = DomUtils.GetElement(VerifyCodeConstants.resendCodeForm);
		resendCodeFormElement.method = this._isCodeAuth ? "get" : "post";

		const sendVerificationEmailFormElement = DomUtils.GetElement(VerifyCodeConstants.sendVerificationEmailForm);
		sendVerificationEmailFormElement.method = this._isCodeAuth ? "get" : "post";

		if (this._isCodeAuth === true) {
			const signInHiddenField = `<input type="hidden" name="signin" value="${this._signIn}" />`;

			resendCodeFormElement.innerHTML = signInHiddenField;
			sendVerificationEmailFormElement.innerHTML = signInHiddenField;
		}
		else {
			resendCodeFormElement.action = `?signin=${this._signIn}`;
			sendVerificationEmailFormElement.action = `?signin=${this._signIn}`;
		}
	}

	/* istanbul ignore next */
	_SetupValidation() {
		ValidatedInput.Init(VerifyCodeConstants.code);
	}

	_SetupRegisterDays(registerDays) {
		DomUtils.ShowHide(registerDays > 0, VerifyCodeConstants.registerDaysContainer);

		if (registerDays > 0) {
			const registerDaysLabelElement = DomUtils.GetElement(VerifyCodeConstants.registerDaysLabel);
			registerDaysLabelElement.innerText = registerDays;
		}
	}

	_ToggleSendEmailVerification(canResend) {
		const didntGetCodeContainerElement = DomUtils.GetElement(VerifyCodeConstants.didntGetCodeContainer);
		const sendEmailVerificationContainer = DomUtils.GetElement(VerifyCodeConstants.sendEmailVerificationContainer);
		const sendEmailVerificationSpacer = DomUtils.GetElement(VerifyCodeConstants.sendEmailVerificationSpacer);
		const loginLinkContainerElement = DomUtils.GetElement(VerifyCodeConstants.loginLinkContainer);

		didntGetCodeContainerElement.style.display = canResend ? "" : "none";
		sendEmailVerificationContainer.style.display = canResend ? "" : "none";
		sendEmailVerificationSpacer.style.display = canResend ? "" : "none";
		loginLinkContainerElement.classList.toggle("center", !canResend);
	}

	_BindEvents() {
		this._BindVerifyCodeButton();
		this._BindResendCodeButton();
		this._BindEmailVerificationLink();
		this._BindEnterKeys();
		this._BindAutocomplete();
		this._BindOTP();
	}

	_BindVerifyCodeButton() {
		const verifyCodeButtonElement = DomUtils.GetElement(VerifyCodeConstants.verifyCodeButton);
		verifyCodeButtonElement.addEventListener("click", this._OnVerifyCodeClicked.bind(this));
	}

	_BindResendCodeButton() {
		const resendCodeButtonElement = DomUtils.GetElement(VerifyCodeConstants.resendCodeButton);
		resendCodeButtonElement.addEventListener("click", this._OnResendCodeClicked.bind(this));
	}

	_BindEmailVerificationLink() {
		const sendEmailVerificationLinkElement = DomUtils.GetElement(VerifyCodeConstants.sendEmailVerificationLink);
		sendEmailVerificationLinkElement.addEventListener("click", this._OnSendEmailVerificationClicked.bind(this));
	}

	_BindEnterKeys() {
		DomUtils.OnEnter(
			VerifyCodeConstants.verifyCodeForm,
			(event) => {
				event.preventDefault();
				return false;
			},
			true
		);
		DomUtils.OnEnter(VerifyCodeConstants.code, this._OnVerifyCodeClicked.bind(this));
	}

	_BindAutocomplete() {
		const isMobile = DeviceUtils.IsMobile();

		const autofillInterval = setInterval(() => {
			const codeElement = DomUtils.GetElement(VerifyCodeConstants.code);
			const autocompleteInputElement = this._GetAutofill();

			if (autocompleteInputElement != null) {
				if (codeElement === autocompleteInputElement) {
					if (!!codeElement.value) {
						clearInterval(autofillInterval);
						if (isMobile) {
							this._OnVerifyCodeClicked();
						}
					}
				}
			}
		}, VerifyCodeConstants.autoCompleteInterval);
	}

	_BindOTP() {
		if (!!window.OTPCredential) {
			const codeElem = DomUtils.GetElement(VerifyCodeConstants.code);

			const ac = new AbortController();
			setTimeout(() => {
				ac.abort();
			}, 1 * 60 * 1000); // Abort OTPCredential after 1 minute

			try {
				navigator.credentials
					.get({
						otp: { transport: ["sms"] },
						signal: ac.signal
					})
					.then((otp) => {
						codeElem.value = otp.code;
						this._OnVerifyCodeClicked();
					});
			}
			catch {
				// DO NOTHING. SUPPRESS OTP TIMEOUT
			}
		}
	}

	_GetAutofill() {
		return document.querySelector("input:autofill");
	}

	_OnVerifyCodeClicked() {
		if (this._Validate()) {
			this._VerifyToken().then((result) => {
				if (result === true) {
					this._formSubmitBlocked = false;
					this._SubmitForm();
				}
			});
		}
	}

	_OnResendCodeClicked() {
		CloakService.Block();

		const resendCodeFormElement = DomUtils.GetElement(VerifyCodeConstants.resendCodeForm);
		resendCodeFormElement.submit();
	}

	_OnSendEmailVerificationClicked() {
		CloakService.Block();

		const sendEmailVerificationFormElement = DomUtils.GetElement(VerifyCodeConstants.sendVerificationEmailForm);
		sendEmailVerificationFormElement.submit();
	}

	_Validate() {
		let result = true;

		if (this._ValidateElement(VerifyCodeConstants.code) != null) {
			this._FocusElement(VerifyCodeConstants.code);
			result = false;
		}

		return result;
	}

	/* istanbul ignore next */
	_SubmitForm() {
		super._SubmitForm(VerifyCodeConstants.verifyCodeForm);
	}

	_VerifyToken() {
		CloakService.Block();

		return new Promise((resolve) => {
			try {
				this._GetRecaptchaToken()
					.then((recaptcha) => {
						const codeElement = DomUtils.GetElement(VerifyCodeConstants.code);
						const code = codeElement.value;

						CloakService.Block();
						this._verifyCodeService
							.Verify(this._verifyEndpoint, code, this._token, recaptcha)
							.then((result) => {
								CloakService.Unblock();
								if (!!result.token) {
									this._SetToken(result.token);
									resolve(true);
								}
								else {
									this._SetInvalidCode(result.errMessage);
									resolve(false);
								}
							})
							.catch((error) => {
								CloakService.Unblock();

								this._SetInvalidCode("ERR_CODE_ERROR");
								console.warn(error);
								resolve(false);
							});
					})
					.catch((error) => {
						CloakService.Unblock();

						this._SetInvalidCode("ERR_CODE_RECAPTCHA");
						console.warn(error);
						resolve(false);
					});
			}
			catch (error) {
				CloakService.Unblock();

				this._SetInvalidCode("ERR_CODE_UNKNOWN");
				console.warn(error);
				resolve(false);
			}
		});
	}

	_SetInvalidCode(message) {
		const codeElement = DomUtils.GetElement(VerifyCodeConstants.code);
		codeElement.value = null;

		codeElement.validate();

		this._FocusElement(codeElement);
		this._ShowMessage(message);
	}

	_SetToken(token) {
		const tokenElement = DomUtils.GetElement(VerifyCodeConstants.token);
		tokenElement.value = token;
	}
}
